import React from 'react';
import { Handshake, Heart, Users } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const Partners = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#E6B933]/10 rounded-full blur-3xl"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <div className="inline-block mb-6 px-6 py-2 bg-[#E6B933]/10 rounded-full border border-[#E6B933]/20">
              <p className="text-[#E6B933] font-semibold text-sm">Community Collaboration</p>
            </div>
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Our <span className="text-[#E6B933]">Partners</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Working together with community organizations to create lasting impact and transform the lives of young people.
            </p>
          </div>
        </div>
      </section>

      {/* Partnership Values */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-6 text-center hover:shadow-lg transition-all duration-300">
              <div className="w-14 h-14 bg-[#E6B933]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Handshake className="text-[#E6B933]" size={28} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Collaboration</h3>
              <p className="text-gray-600">Joining forces to maximize our collective impact on youth development</p>
            </Card>
            <Card className="p-6 text-center hover:shadow-lg transition-all duration-300">
              <div className="w-14 h-14 bg-[#E6B933]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Users className="text-[#E6B933]" size={28} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Community</h3>
              <p className="text-gray-600">Building stronger neighborhoods through shared vision and resources</p>
            </Card>
            <Card className="p-6 text-center hover:shadow-lg transition-all duration-300">
              <div className="w-14 h-14 bg-[#E6B933]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Heart className="text-[#E6B933]" size={28} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Impact</h3>
              <p className="text-gray-600">Creating positive, lasting change in the lives of young people</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Featured Partners */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Featured <span className="text-[#E6B933]">Partners</span>
            </h2>
          </div>

          <div className="space-y-8">
            {/* Missing Element Sports Performance & Fitness */}
            <Card className="p-8 md:p-12 hover:shadow-xl transition-all duration-300 border-l-4 border-[#E6B933] bg-white" data-testid="partner-missing-element">
              <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
                <div className="flex-shrink-0">
                  <a href="https://www.missingelementsports.com" target="_blank" rel="noopener noreferrer">
                    <div className="w-64 h-64 rounded-2xl overflow-hidden bg-black shadow-2xl">
                      <img 
                        src="https://static.wixstatic.com/media/eea016_94f01cfe286e40509814548612544011~mv2.jpg/v1/fill/w_462,h_412,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/7A3BCB47-AA66-4140-A568-8352DB00A10C_edi.jpg" 
                        alt="Missing Element Sports Performance & Fitness" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </a>
                </div>
                <div className="flex-1 text-center lg:text-left">
                  <h3 className="text-3xl font-bold text-gray-900 mb-6" data-testid="partner-me-title">Missing Element Sports Performance & Fitness</h3>
                  <p className="text-lg text-gray-600 leading-relaxed mb-4">
                    Missing Element Sports Performance & Fitness is a premier sports training and fitness facility in Spring, Texas, where athletes and non-athletes enter to adapt, evolve, and transform. Specializing in youth, high school, college, and professional athletes, they have trained over 500+ athletes — many of whom have gone on to compete at the MLB, NFL, NBA, WNBA, NCAA, and USA Track & Field levels.
                  </p>
                  <p className="text-lg text-gray-600 leading-relaxed mb-6">
                    Their programs include speed development bootcamps, all-sport athletic development, football-specific training, and group fitness classes. As a key partner of The WHY ME Foundation, Missing Element provides the athletic development infrastructure that powers our youth sports initiatives and the Big Ike Revival program.
                  </p>
                  <div className="flex flex-wrap gap-3 justify-center lg:justify-start mb-6">
                    <span className="px-4 py-2 bg-[#E6B933]/10 rounded-full text-[#E6B933] font-semibold text-sm">Sports Performance</span>
                    <span className="px-4 py-2 bg-[#E6B933]/10 rounded-full text-[#E6B933] font-semibold text-sm">Speed Development</span>
                    <span className="px-4 py-2 bg-[#E6B933]/10 rounded-full text-[#E6B933] font-semibold text-sm">Youth Athletics</span>
                    <span className="px-4 py-2 bg-[#E6B933]/10 rounded-full text-[#E6B933] font-semibold text-sm">Health & Fitness</span>
                    <span className="px-4 py-2 bg-[#E6B933]/10 rounded-full text-[#E6B933] font-semibold text-sm">500+ Athletes Trained</span>
                  </div>
                  <a href="https://www.missingelementsports.com" target="_blank" rel="noopener noreferrer">
                    <Button className="bg-[#E6B933] hover:bg-[#D4A01F] text-black font-bold px-6" data-testid="partner-me-website-btn">
                      Visit Website
                    </Button>
                  </a>
                </div>
              </div>
            </Card>

            {/* Elite Boys and Girls Club */}
            <Card className="p-8 md:p-12 hover:shadow-xl transition-all duration-300 border-l-4 border-[#E6B933] bg-white" data-testid="partner-elite-bgc">
            <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
              <div className="flex-shrink-0">
                <div className="w-64 h-64 rounded-2xl overflow-hidden bg-black flex items-center justify-center shadow-2xl">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_foundation-web-5/artifacts/ajs805za_6832751075169440476.JPG" 
                    alt="Elite Boys and Girls Club Logo" 
                    className="w-full h-full object-contain p-4"
                  />
                </div>
              </div>
              <div className="flex-1 text-center lg:text-left">
                <h3 className="text-3xl font-bold text-gray-900 mb-6">Elite Boys and Girls Club</h3>
                <p className="text-lg text-gray-600 leading-relaxed mb-4">
                  The Elite Boys and Girls Club is a youth development organization committed to building confidence, character, and leadership in young people. The organization provides structured mentorship, guidance, and positive engagement opportunities designed to support personal and academic growth.
                </p>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  Through collaboration with organizations like The WHY ME Foundation, the club works to create positive, lasting impact within the community.
                </p>
                <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
                  <span className="px-4 py-2 bg-[#E6B933]/10 rounded-full text-[#E6B933] font-semibold text-sm">Youth Development</span>
                  <span className="px-4 py-2 bg-[#E6B933]/10 rounded-full text-[#E6B933] font-semibold text-sm">Mentorship</span>
                  <span className="px-4 py-2 bg-[#E6B933]/10 rounded-full text-[#E6B933] font-semibold text-sm">Leadership</span>
                  <span className="px-4 py-2 bg-[#E6B933]/10 rounded-full text-[#E6B933] font-semibold text-sm">Character Building</span>
                </div>
              </div>
            </div>
          </Card>
          </div>
        </div>
      </section>

      {/* Partnership CTA */}
      <section className="py-20 bg-black text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#E6B933] rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#E6B933] rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl font-bold mb-6">
            Become a <span className="text-[#E6B933]">Partner</span>
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Interested in partnering with The WHY ME Foundation? We're always looking for organizations that share our commitment to youth development and community impact.
          </p>
          <Link to="/get-involved">
            <Button className="bg-[#E6B933] hover:bg-[#D4A01F] text-black font-bold px-8 py-6 text-lg transition-all duration-300 hover:scale-105">
              Partner With Us
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Partners;
